import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SwitchHorizontal01 } from '@untitledui/icons';
import type { FormEvent } from 'react';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { ApiError, getErrorMessage } from '@/lib/errors';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';

import { ActiveCampaignIcon } from '../ActiveCampaignIcon';
import { connectActiveCampaign } from '../api/connect';

interface ConnectActiveCampaignFormProps {
  orgId: number | string;
  disabled?: boolean;
}

const AC_CONNECT_ERROR_MESSAGES: Record<string, string> = {
  rate_limited:
    'ActiveCampaign is rate limiting requests. Please wait a moment and try again.',
  connect_race:
    'Another connection attempt is already in progress. Please wait and try again.',
  validation_failed:
    'Invalid ActiveCampaign credentials. Please check your account URL and API key.',
};

const CONNECTOR_TILE_CLASS =
  'flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs';

const CARD_CLASS =
  'max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6';

const API_URL_HINT =
  'Found in ActiveCampaign under Settings → Developer, next to your API key.';

export function ConnectActiveCampaignForm({
  orgId,
  disabled,
}: ConnectActiveCampaignFormProps) {
  const [apiUrl, setApiUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const queryClient = useQueryClient();

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: () =>
      connectActiveCampaign(orgId, {
        api_url: apiUrl.trim(),
        api_token: apiToken.trim(),
      }),
    onSuccess: () => {
      setApiToken('');
      setApiUrl('');
      void queryClient.invalidateQueries({
        queryKey: ['activeCampaignStatus', orgId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['activeCampaignSyncHealth', orgId],
      });
    },
  });

  const handleUrlChange = useCallback((value: string) => {
    setApiUrl(value);
  }, []);

  const handleTokenChange = useCallback((value: string) => {
    setApiToken(value);
  }, []);

  const isSubmitDisabled =
    disabled || isPending || !apiUrl.trim() || !apiToken.trim();

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      // Enter-key submission can fire even while the button is disabled, so
      // guard with the exact same conditions to avoid a blank/pending connect.
      if (isSubmitDisabled) {
        return;
      }
      mutate();
    },
    [isSubmitDisabled, mutate]
  );

  const code =
    error instanceof ApiError
      ? (error.response as { error_code?: string } | undefined)?.error_code
      : undefined;
  const message =
    (code && AC_CONNECT_ERROR_MESSAGES[code]) ||
    (error instanceof ApiError ? error.message : undefined) ||
    getErrorMessage(error);

  return (
    <div className={CARD_CLASS}>
      {/* Connector visual: ActiveCampaign ⇄ Ahoy, two-way. */}
      <div className='flex items-center gap-3'>
        <span className={CONNECTOR_TILE_CLASS}>
          <ActiveCampaignIcon className='h-6 w-6 text-[var(--color-text-primary)]' />
        </span>
        <SwitchHorizontal01
          className='h-5 w-5 text-[var(--color-text-tertiary)]'
          aria-label='two-way sync'
        />
        <span className={CONNECTOR_TILE_CLASS}>
          <AhoyLogoMinimal className='h-6 w-6' />
        </span>
      </div>

      <div className='mt-5 space-y-1'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          Connect your ActiveCampaign account
        </h3>
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          Link an ActiveCampaign account to keep it in two-way sync with your
          Ahoy contacts — new subscribers, profile edits and unsubscribes flow
          automatically in both directions.
        </p>
      </div>

      <form onSubmit={handleSubmit} className='mt-5 flex flex-col gap-4'>
        <Input
          label='API URL'
          type='url'
          placeholder='https://youraccount.api-us1.com'
          isRequired
          isDisabled={isPending}
          value={apiUrl}
          onChange={handleUrlChange}
          hint={API_URL_HINT}
        />
        <Input
          label='API key'
          type='password'
          autoComplete='off'
          isRequired
          isDisabled={isPending}
          value={apiToken}
          onChange={handleTokenChange}
        />

        <p className='text-body-small text-[var(--color-text-secondary)]'>
          We recommend creating a dedicated ActiveCampaign service user for this
          key — API keys are tied to a user and stop working if that user is
          ever deleted. See the{' '}
          <a
            href='https://help.activecampaign.com/hc/en-us/articles/207317590'
            target='_blank'
            rel='noopener noreferrer'
            className='underline'
          >
            ActiveCampaign API docs
          </a>{' '}
          for where to find your URL and key.
        </p>

        {isError && (
          <p role='alert' className='text-sm text-utility-error-700'>
            {message}
          </p>
        )}

        <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
          <Button
            type='submit'
            color='primary'
            size='md'
            isDisabled={isSubmitDisabled}
            isLoading={isPending}
            showTextWhileLoading
          >
            {isPending ? 'Connecting…' : 'Connect ActiveCampaign'}
          </Button>
        </div>
      </form>
    </div>
  );
}
